import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { apiRequest } from "../api/client";
import { Button, Checkbox, Input, Select } from "../ds";

interface FilterValues {
  values: string[];
  total: number;
  has_more: boolean;
  truncated: boolean;
  status: string;
}

export function TextFilterValuePicker({ datasetId, field, multiple, selected, onChange }: {
  datasetId: string;
  field: string;
  multiple: boolean;
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  useEffect(() => { const timer = window.setTimeout(() => setNeedle(search), 250); return () => window.clearTimeout(timer); }, [search]);
  const values = useQuery({
    queryKey: ["studio-filter-values", datasetId, field, needle],
    queryFn: () => apiRequest<FilterValues>(`/api/v1/owner/datasets/${datasetId}/filter-values?field=${encodeURIComponent(field)}&search=${encodeURIComponent(needle)}`),
    staleTime: 60_000,
    retry: 1,
  });
  const options = values.data?.values ?? [];
  const unavailable = values.isError || values.data?.status === "unavailable";
  const label = (value: string) => value === "" ? "(Empty text)" : value;
  const warning = values.data?.status === "delayed" ? "Showing saved values; the source could not refresh." : values.data?.truncated ? "Values come from a limited source read; some may be missing." : null;
  return <div className="ds-stack" style={{ gap: 6 }}>
    {multiple ? <details className="st-filter-values" style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 6 }}>
      <summary style={{ cursor: "pointer", fontSize: 12.5 }} aria-label="Choose filter values">{selected.length ? `${selected.length} values selected` : "Choose values…"}</summary>
      <Input inputSize="sm" aria-label="Search filter values" placeholder="Search values…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ marginTop: 6 }} />
      <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
        {options.map((value) => <Checkbox key={value} label={label(value)} checked={selected.includes(value)} onChange={(checked) => onChange(checked ? [...selected, value] : selected.filter((v) => v !== value))} />)}
      </div>
      {!values.isPending && !unavailable && !options.length ? <div className="ds-small ds-muted">No matching values.</div> : null}
      {values.data?.has_more ? <div className="ds-small ds-muted">Showing {options.length} of {values.data.total} matching values. Search to narrow the list.</div> : null}
    </details> : <>
      <Input inputSize="sm" aria-label="Search filter values" placeholder="Search values…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <Select inputSize="sm" aria-label="Filter value" value={selected.length ? JSON.stringify(selected[0]) : ""} disabled={unavailable} onChange={(e) => onChange(e.target.value ? [JSON.parse(e.target.value) as string] : [])}>
        <option value="">Choose a value…</option>
        {[...selected.filter((v) => !options.includes(v)), ...options].map((value) => <option key={value} value={JSON.stringify(value)}>{label(value)}</option>)}
      </Select>
      {values.data?.has_more ? <div className="ds-small ds-muted">Search to find more values.</div> : null}
    </>}
    {multiple && selected.length ? <div className="ds-stack" style={{ gap: 3 }}>
      {selected.map((value) => <div key={value} className="ds-row" style={{ gap: 4 }}><span className="ds-small ds-grow">{label(value)}</span><button type="button" className="st-mini" aria-label={`Remove ${label(value)}`} onClick={() => onChange(selected.filter((v) => v !== value))}>×</button></div>)}
    </div> : null}
    {values.isFetching ? <div className="ds-small ds-muted">Loading values…</div> : null}
    {unavailable ? <div className="ds-small ds-muted">Couldn’t load values. <Button size="sm" onClick={() => void values.refetch()}>Retry</Button></div> : null}
    {warning ? <div className="ds-small ds-muted">{warning}</div> : null}
    <div className="ds-small ds-faint">Use “is blank” or “is not blank” to filter missing values.</div>
  </div>;
}
